import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { getFamilies, FamilyListItem, getFamilyDetails, FamilyDetails } from '../../api/admin';

const FamiliesPage: React.FC = () => {
  const navigate = useNavigate();
  const [families, setFamilies] = useState<FamilyListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState<FamilyDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    loadFamilies();
  }, []);

  const loadFamilies = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchQuery || undefined,
        limit: 100,
      };
      const data = await getFamilies(params);
      setFamilies(data.families);
      setTotal(data.total);
      setError(null);
    } catch (err: any) {
      console.error('[FamiliesPage] Error loading families:', err);
      setError(err.response?.data?.message || 'Failed to load families');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadFamilies();
  };

  const openDetailsDialog = async (familyId: number) => {
    setLoadingDetails(true);
    setDetailsDialogOpen(true);
    try {
      const details = await getFamilyDetails(familyId);
      setSelectedFamily(details);
    } catch (err: any) {
      console.error('[FamiliesPage] Error loading family details:', err);
      setError(err.response?.data?.message || 'Failed to load family details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedFamily(null);
  };

  if (loading && families.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/dashboard')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">Family Management</Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <Paper elevation={2} sx={{ mb: 3, p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Search by family name"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ flexGrow: 1 }}
          />
          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Total families: {total}
        </Typography>
      </Paper>

      {/* Families Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Family Name</TableCell>
              <TableCell>Primary Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Students</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {families.map((family) => (
              <TableRow key={family.id}>
                <TableCell>{family.id}</TableCell>
                <TableCell>{family.family_name}</TableCell>
                <TableCell>
                  {family.primary_contact.first_name} {family.primary_contact.last_name}
                </TableCell>
                <TableCell>{family.primary_contact.email}</TableCell>
                <TableCell>
                  <Chip label={family.student_count} color="primary" size="small" />
                </TableCell>
                <TableCell>{new Date(family.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => openDetailsDialog(family.id)}
                    title="View Details"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Family Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={closeDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle>Family Details</DialogTitle>
        <DialogContent>
          {loadingDetails && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!loadingDetails && selectedFamily && (
            <Box sx={{ mt: 2 }}>
              {/* Family Info */}
              <Typography variant="h6" gutterBottom>
                {selectedFamily.primaryContact.first_name} {selectedFamily.primaryContact.last_name} Family
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status: {selectedFamily.status}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Primary Contact: {selectedFamily.primaryContact.first_name}{' '}
                {selectedFamily.primaryContact.last_name} ({selectedFamily.primaryContact.email})
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* Students List */}
              <Typography variant="h6" gutterBottom>
                Students ({selectedFamily.students?.length || 0})
              </Typography>
              {selectedFamily.students && selectedFamily.students.length > 0 ? (
                <List>
                  {selectedFamily.students.map((student) => (
                    <ListItem key={student.id}>
                      <ListItemText
                        primary={`${student.first_name} ${student.last_name}`}
                        secondary={
                          <>
                            Grade: {student.grade} | Pickup: {student.pickup_type}
                            {student.medical_notes && (
                              <> | Medical Notes: {student.medical_notes}</>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">No students registered</Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Authorized Persons List */}
              <Typography variant="h6" gutterBottom>
                Authorized Pickup Persons ({selectedFamily.authorizedPersons?.length || 0})
              </Typography>
              {selectedFamily.authorizedPersons && selectedFamily.authorizedPersons.length > 0 ? (
                <List>
                  {selectedFamily.authorizedPersons.map((person) => (
                    <ListItem key={person.id}>
                      <ListItemText
                        primary={`${person.first_name} ${person.last_name}`}
                        secondary={
                          <>
                            {person.relationship} | {person.phone}
                            {person.email && ` | ${person.email}`}
                            {person.has_pickup_rights && ' | Can pickup'}
                            {person.id_check_required && ' | ID required'}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">No authorized persons registered</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailsDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FamiliesPage;
